import { useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, KeyRound, Mail, Phone, Search, X } from "lucide-react";
import { fuzzyMatch } from "../../lib/searchUtils";
import { formatPhoneNumber } from "../../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
const UsersTab = ({ users, currentUser, getAuthHeaders, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", username: "", role: "office_manager", email: "", phone: "",
  });

  const resetForm = () => {
    setForm({ name: "", username: "", role: "office_manager", email: "", phone: "" });
    setEditingUser(null);
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setForm({ 
      name: u.name, 
      username: u.username, 
      role: u.role,
      email: u.email || "",
      phone: u.phone || "",
    });
    setDialogOpen(true);
  };

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, '');
    const d = cleaned.length === 11 && cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return phone;
  };

  const formatPhoneForStorage = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, '');
    const d = cleaned.length === 11 && cleaned.startsWith('1') ? cleaned.slice(1) : cleaned;
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return phone;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...form,
        phone: formatPhoneForStorage(form.phone),
        email: form.email || null
      };
      
      if (editingUser) {
        await apiClient.put(`/users/${editingUser.id}`, submitData);
        toast.success("User updated");
      } else {
        await apiClient.post(`/users`, submitData);
        toast.success("User created with initial password: V2RMasonry!");
      }
      onRefresh();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/users/${id}`);
      toast.success("User deleted");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResetPassword = async (user) => {
    try {
      await apiClient.post(`/users/${user.id}/reset-password`);
      toast.success(`Password reset for ${user.name}. New password: V2RMasonry!`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-800",
      project_manager: "bg-blue-100 text-blue-800",
      foreman: "bg-green-100 text-green-800",
      office_manager: "bg-gray-100 text-gray-800",
    };
    const labels = {
      admin: "Admin", project_manager: "Project Manager",
      foreman: "Foreman", office_manager: "Office Manager",
    };
    return <span className={`badge-status ${colors[role]}`}>{labels[role] || role}</span>;
  };

  // Sort users: 1st by role (admin, project_manager, office_manager, foreman), then alphabetically by name
  const roleOrder = { admin: 0, project_manager: 1, office_manager: 2, foreman: 3 };
  const sortedUsers = [...users].sort((a, b) => {
    const roleA = roleOrder[a.role] ?? 99;
    const roleB = roleOrder[b.role] ?? 99;
    if (roleA !== roleB) return roleA - roleB;
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  }).filter((u) => {
    if (!search.trim()) return true;
    const searchable = [u.name, u.username, u.role, u.email, u.phone].filter(Boolean).join(" ");
    return fuzzyMatch(search, searchable);
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Users</CardTitle>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />Add User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
              <DialogDescription>
                {editingUser ? "Update user details and contact information." : "Create a new user account."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-group">
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="user-name-input" />
              </div>
              <div className="form-group">
                <Label>User ID *</Label>
                <Input type="text" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} required data-testid="user-username-input" placeholder="e.g., jsmith" />
              </div>
              <div className="form-group">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="project_manager">Project Manager</SelectItem>
                    <SelectItem value="foreman">Foreman</SelectItem>
                    <SelectItem value="office_manager">Office Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-3">Contact Information (for notifications)</p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="form-group">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Label>
                    <Input 
                      type="email" 
                      value={form.email} 
                      onChange={(e) => setForm({...form, email: e.target.value})} 
                      placeholder="user@example.com"
                      data-testid="user-email-input" 
                    />
                  </div>
                  <div className="form-group">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone
                    </Label>
                    <Input 
                      type="tel" 
                      value={form.phone} 
                      onChange={(e) => setForm({...form, phone: formatPhoneNumber(e.target.value)})} 
                      placeholder="(555) 123-4567"
                      data-testid="user-phone-input" 
                    />
                  </div>
                </div>
              </div>
              
              {!editingUser && (
                <>
                  <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                    <p className="font-medium text-foreground">Initial Password</p>
                    <p>New users will be assigned the password: <code className="bg-background px-1 rounded">V2RMasonry!</code></p>
                    <p>They will be required to change it on first login.</p>
                  </div>
                </>
              )}
              <Button type="submit" className="w-full" data-testid="submit-user-btn">
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="p-0">
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="pl-9 pr-9" data-testid="users-search-input" />
            {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Name</TableHead>
              <TableHead>User ID</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((u) => (
              <TableRow key={u.id} className="table-row">
                <TableCell className="font-medium">
                  {u.name} 
                  {u.id === currentUser?.id && <span className="text-xs text-muted-foreground ml-1">(You)</span>}
                  {u.must_change_password && <span className="text-xs text-amber-600 ml-2">(Password Reset Required)</span>}
                </TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell>{getRoleBadge(u.role)}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 text-sm">
                    {u.email && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Mail className="w-3 h-3" /> {u.email}
                      </span>
                    )}
                    {u.phone && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="w-3 h-3" /> {formatPhoneForDisplay(u.phone)}
                      </span>
                    )}
                    {!u.email && !u.phone && (
                      u.id === currentUser?.id ? (
                        <Link 
                          to="/profile" 
                          className="text-xs text-primary hover:underline"
                          data-testid={`add-contact-${u.id}`}
                        >
                          Add contact info →
                        </Link>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">No contact info</span>
                      )
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`} title="Edit User">
                      <Edit className="w-4 h-4" />
                    </Button>
                    {u.id !== currentUser?.id && (
                      <>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-amber-600" data-testid={`reset-password-${u.id}`} title="Reset Password">
                              <KeyRound className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Reset Password?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will reset {u.name}&apos;s password to the initial password: <strong>V2RMasonry!</strong>
                                <br /><br />
                                They will be required to change it on their next login.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleResetPassword(u)}>Reset Password</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive" data-testid={`delete-user-${u.id}`} title="Delete User">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User?</AlertDialogTitle>
                              <AlertDialogDescription>This will permanently remove {u.name}.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(u.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default UsersTab;
